import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Jurisdicao, JurisdicaoFormData } from '../regrasJuridicasModel'

interface Props {
    open: boolean
    initial: Jurisdicao | null
    onClose: () => void
    onSaved: () => void
}

const VAZIO: JurisdicaoFormData = { codigo: '', nome: '', pais: '', regiao: '', regimeJuridico: '', activa: true }

/** Cria ou altera uma jurisdição (o "país/regime" a que as regras de assembleia pertencem). */
export default function JurisdicaoFormDialog({ open, initial, onClose, onSaved }: Props) {
    const editing = initial !== null
    const [form, setForm] = useState<JurisdicaoFormData>(VAZIO)
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!open) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dos campos quando o diálogo abre
        setApiError('')
        setForm(initial ? {
            codigo: initial.codigo,
            nome: initial.nome,
            pais: initial.pais ?? '',
            regiao: initial.regiao ?? '',
            regimeJuridico: initial.regimeJuridico ?? '',
            activa: initial.activa,
        } : VAZIO)
    }, [open, initial])

    const set = <K extends keyof JurisdicaoFormData>(campo: K, valor: JurisdicaoFormData[K]) =>
        setForm((anterior) => ({ ...anterior, [campo]: valor }))

    const submit = async () => {
        if (!form.codigo.trim() || !form.nome.trim()) {
            setApiError('O código e o nome são obrigatórios.')
            return
        }
        setSubmitting(true)
        setApiError('')
        try {
            const corpo = {
                codigo: form.codigo.trim(),
                nome: form.nome.trim(),
                pais: form.pais.trim().toUpperCase(),
                regiao: form.regiao.trim() || null,
                regimeJuridico: form.regimeJuridico.trim() || null,
                activa: form.activa,
            }
            if (editing) await api.put(`/api/admin/regras-juridicas/jurisdicoes/${initial.id}`, corpo)
            else await api.post('/api/admin/regras-juridicas/jurisdicoes', corpo)
            onSaved()
            onClose()
        } catch (err) {
            const body = getBackendError(err)
            setApiError(body?.fields ? Object.values(body.fields).join(' · ') : body?.message ?? 'Não foi possível guardar a jurisdição.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontSize: '1.05rem', fontWeight: 600 }}>
                {editing ? 'Editar jurisdição' : 'Nova jurisdição'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                    {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}
                    <TextField
                        label="Código"
                        value={form.codigo}
                        onChange={(e) => set('codigo', e.target.value)}
                        disabled={editing}
                        helperText={editing ? 'O código não se altera.' : 'Identificador curto e estável (letras, números, - e _).'}
                        autoFocus={!editing}
                        required
                    />
                    <TextField label="Nome" value={form.nome} onChange={(e) => set('nome', e.target.value)} required />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 2 }}>
                        <TextField
                            label="País (ISO)"
                            value={form.pais}
                            onChange={(e) => set('pais', e.target.value)}
                            slotProps={{ htmlInput: { maxLength: 2 } }}
                            helperText="2 letras, ex.: PT, MZ."
                        />
                        <TextField label="Região" value={form.regiao} onChange={(e) => set('regiao', e.target.value)} />
                    </Box>
                    <TextField
                        label="Regime jurídico"
                        value={form.regimeJuridico}
                        onChange={(e) => set('regimeJuridico', e.target.value)}
                        helperText="Por exemplo, a lei da propriedade horizontal aplicável. Texto livre, só para identificação."
                    />
                    <FormControlLabel
                        control={<Switch checked={form.activa} onChange={(e) => set('activa', e.target.checked)} />}
                        label="Activa (pode ser atribuída a condomínios)"
                    />
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Criar a jurisdição não cria regras. Os condomínios que a escolherem ficam sem regra aplicável
                        até haver uma versão validada.
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={submitting} color="inherit">Cancelar</Button>
                <Button onClick={submit} variant="contained" disabled={submitting}>
                    {submitting ? 'A guardar…' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
